import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Logo } from '@guildeducationinc/recess';
import ExpandMoreIcon from '@guildeducationinc/recess/icons/ExpandMoreIcon';
import { useAccessibleClients } from '@src/hooks/useAccessibleClients';
import { useAuth } from '@src/hooks/useAuth';
import { useCurrentClient } from '@src/hooks/useCurrentClient';
import classNames from 'classnames';
import { getRegionAndClient, TrackableButton, useGetS3Url, useLocale } from 'pe-core-frontend';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './ClientSwitcher.module.scss';
import { getClientOptionSx, triggerButtonSx, viewAllLinkSx } from './styles';
/**
 * Logo link component that shows Guild logo and optional client logo.
 * Extracted to avoid repetition between single-client and multi-client views.
 */
const LogoLink = ({ logoUrl, clientName, }) => (_jsxs(Link, { to: '/', className: styles.logoWrapper, children: [_jsx(Logo, { alt: 'Guild Education', cobranded: true }), logoUrl && (_jsx("img", { className: styles.clientLogo, src: logoUrl, alt: clientName || 'Employer logo' }))] }));
/**
 * Renders a single client option in the dropdown.
 */
const ClientOption = ({ client, isEmployee, isCurrent, isSigningIn, disabled, onSelect, t, }) => {
    const trackingTag = isEmployee
        ? `client_switcher_employee_${client.code}`
        : `client_switcher_approver_${client.code}`;
    return (_jsxs(TrackableButton, { btnType: 'mui', enText: client.name, tag: trackingTag, onClick: e => !isCurrent && !disabled && onSelect(e, client, isEmployee), disableRipple: true, sx: getClientOptionSx({ isCurrent, isEmployee, disabled }), children: [_jsx("div", { className: styles.clientItemName, children: client.name }), isEmployee ? (isSigningIn && (_jsx("div", { className: styles.requestsCount, children: _jsx("span", { children: t('Signing in...') }) }))) : (_jsxs("div", { className: styles.requestsCount, children: [!isSigningIn && client.pendingApprovalsCount > 0 && (_jsx("span", { className: styles.statusDot })), _jsx("span", { children: isSigningIn
                            ? t('Signing in...')
                            : t('{count} requests ready for review', {
                                values: { count: client.pendingApprovalsCount },
                            }) })] }))] }));
};
/**
 * Client switcher dropdown component for the header.
 * Combines the logo and client name as a single clickable trigger.
 * Shows dropdown to switch between accessible clients.
 * If the user only has one client, just shows logo and client name without dropdown.
 */
export const ClientSwitcher = ({ className, }) => {
    const [open, setOpen] = useState(false);
    const [selectedClientCode, setSelectedClientCode] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const { currentUser } = useAuth();
    const { employeeClients, approverClients, loading } = useAccessibleClients();
    const { t } = useLocale();
    const { currentClient } = useCurrentClient();
    const { s3Url: logoUrl } = useGetS3Url({ s3Key: currentClient?.logoS3Key });
    const currentClientName = currentUser?.clientInfo?.name || '';
    const currentClientId = currentUser?.clientId;
    // Filter out employee clients that also have an approver role for the same clientId
    // (prefer showing the approver card over the employee card)
    const approverClientIds = new Set(approverClients.map(c => c.clientId));
    const filteredEmployeeClients = employeeClients.filter(c => !approverClientIds.has(c.clientId));
    const hasMultipleClients = filteredEmployeeClients.length + approverClients.length > 1;
    const hasEmployeeAndApproverSections = filteredEmployeeClients.length > 0 && approverClients.length > 0;
    /**
     * Handles keyboard interactions for the dropdown button.
     * Note: Escape key is handled by the global document listener in useEffect.
     */
    const handleOnKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(!open);
        }
    };
    // Handle click outside and escape key
    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
            }
        };
        const handleOutsideClick = (e) => {
            if (!panelRef.current?.contains(e.target) &&
                !triggerRef.current?.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscapeKey);
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchend', handleOutsideClick);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchend', handleOutsideClick);
        };
    }, []);
    /**
     * Handles clicking on a client to switch to it.
     * Redirects to SAML login with return_to pointing to the selected client.
     * Employees are routed to the root path, approvers are routed to /tasks.
     */
    const handleClientSelect = (e, client, isEmployee) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedClientCode(client.code);
        const { region } = getRegionAndClient();
        const path = isEmployee ? '/' : '/tasks';
        const returnTo = encodeURIComponent(`/${region}/${client.code}${path}`);
        window.location.href = `/${region}/auth/saml/login?return_to=${returnTo}`;
    };
    /**
     * Handles clicking "View all workspaces" link.
     * Passes ?client param so SelectClient knows user came from the switcher (shows logout, not back).
     */
    const handleViewAllWorkspaces = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { region, client } = getRegionAndClient();
        window.location.href = `/${region}/${client}/select-client?client=${client}`;
    };
    /**
     * Handles trigger click to toggle dropdown.
     */
    const handleButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!open);
    };
    // Don't render if still loading
    if (loading) {
        return null;
    }
    // If only one client, just show logo and client name without dropdown
    if (!hasMultipleClients) {
        return (_jsx("div", { className: classNames(styles.clientSwitcher, className), children: _jsxs("div", { className: styles.triggerStatic, children: [_jsx(LogoLink, { logoUrl: logoUrl, clientName: currentClient?.name }), currentClientName && _jsx("span", { className: styles.clientNameOnly, children: currentClientName })] }) }));
    }
    return (_jsxs("div", { className: classNames(styles.clientSwitcher, className), children: [_jsx(LogoLink, { logoUrl: logoUrl, clientName: currentClient?.name }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Client Switcher', tag: 'client_switcher_toggle', ref: triggerRef, "aria-controls": 'client-switcher-panel', "aria-expanded": open, "aria-haspopup": true, id: 'client-switcher-trigger', onClick: handleButtonClick, onKeyDown: handleOnKeyDown, disableRipple: true, sx: triggerButtonSx, children: _jsxs("span", { className: styles.clientNamePill, children: [_jsx("span", { className: styles.clientName, children: currentClientName }), _jsx(ExpandMoreIcon, { className: classNames(styles.dropdownArrow, { [styles.open]: open }), color: 'black', size: 20 })] }) }), open && (_jsx("div", { ref: panelRef, className: styles.panelContainer, children: _jsxs("div", { className: styles.clientPanel, children: [filteredEmployeeClients.map(client => (_jsx(ClientOption, { client: client, isEmployee: true, isCurrent: client.clientId === currentClientId, isSigningIn: selectedClientCode === client.code, disabled: selectedClientCode !== null, onSelect: handleClientSelect, t: t }, client.compositeId))), hasEmployeeAndApproverSections && _jsx("div", { className: styles.sectionDivider }), approverClients.map(client => (_jsx(ClientOption, { client: client, isEmployee: false, isCurrent: client.clientId === currentClientId, isSigningIn: selectedClientCode === client.code, disabled: selectedClientCode !== null, onSelect: handleClientSelect, t: t }, client.compositeId))), _jsx(TrackableButton, { btnType: 'mui', enText: 'View all workspaces', tag: 'client_switcher_view_all', onClick: handleViewAllWorkspaces, disabled: selectedClientCode !== null, disableRipple: true, sx: viewAllLinkSx, children: t('View all workspaces') })] }) }))] }));
};
// NavBar looks for children with displayName="Brand" to show in mobile view
ClientSwitcher.displayName = 'Brand';
